import {
  COLORS,
  Y_AXIS_TICK_RATE,
  MINIMUM_MONTH_LABEL_GAP,
  DEFAULT_SMOOTHING_INTERVAL,
} from './constants';
import CubicSpline from 'cubic-spline';

// N-Day Data Aggregation for Smoother Trends
const aggregateDataByDays = (
  dataFromGoogleSheets,
  dayInterval = DEFAULT_SMOOTHING_INTERVAL,
) => {
  if (!dataFromGoogleSheets || dataFromGoogleSheets.length === 0) {
    return [];
  }

  const groups = [];
  let currentGroup = null;

  dataFromGoogleSheets.forEach((row) => {
    const date = new Date(row.Date);

    // Calculate group start based on interval
    const daysSinceEpoch = Math.floor(date.getTime() / (1000 * 60 * 60 * 24));
    const groupIndex = Math.floor(daysSinceEpoch / dayInterval);
    const groupKey = `group_${groupIndex}`;

    if (!currentGroup || currentGroup.groupKey !== groupKey) {
      // Start new group
      currentGroup = {
        groupKey,
        groupStart: date.toISOString(),
        groupEnd: date.toISOString(),
        dates: [row.Date],
        estimates: [row.Estimate],
        p5Values: [row['5th Percentile']],
        p95Values: [row['95th Percentile']],
        projectedFlags: [row.isProjected || false],
        count: 1,
      };
      groups.push(currentGroup);
    } else {
      // Add to existing group
      currentGroup.groupEnd = date.toISOString();
      currentGroup.dates.push(row.Date);
      currentGroup.estimates.push(row.Estimate);
      currentGroup.p5Values.push(row['5th Percentile']);
      currentGroup.p95Values.push(row['95th Percentile']);
      currentGroup.projectedFlags.push(row.isProjected || false);
      currentGroup.count++;
    }
  });

  // Calculate group medians and determine if group is projected (any point projected = group projected)
  return groups.map((group) => ({
    Date: group.dates[group.dates.length - 1],
    Estimate: Math.round(median(group.estimates)),
    '5th Percentile': Math.round(median(group.p5Values)),
    '95th Percentile': Math.round(median(group.p95Values)),
    isProjected: group.projectedFlags.some((flag) => flag),
    _groupInfo: {
      groupStart: group.groupStart,
      groupEnd: group.groupEnd,
      dataPointsCount: group.count,
      dayInterval,
    },
  }));
};

// Helper function to calculate median (more stable than mean for outliers)
const median = (arr) => {
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

// Interpolate daily values from aggregated data using cubic splines
const interpolateDailyValues = (originalData, aggregatedData) => {
  // Create arrays of all original dates for reference
  const allOriginalDates = originalData.map((row) => row.Date);

  // Extract aggregated data for interpolation
  const aggregatedDates = aggregatedData.map((row) =>
    new Date(row.Date).getTime(),
  );
  const aggregatedEstimates = aggregatedData.map((row) => row.Estimate);
  const aggregatedP5 = aggregatedData.map((row) => row['5th Percentile']);
  const aggregatedP95 = aggregatedData.map((row) => row['95th Percentile']);

  // Create cubic splines for each data series
  const estimateSpline = new CubicSpline(aggregatedDates, aggregatedEstimates);
  const p5Spline = new CubicSpline(aggregatedDates, aggregatedP5);
  const p95Spline = new CubicSpline(aggregatedDates, aggregatedP95);

  // Interpolate values for all original dates
  return allOriginalDates.map((dateStr) => {
    const dateTime = new Date(dateStr).getTime();

    // Clamp to the range of our aggregated data to avoid extrapolation issues
    const minAggregatedTime = Math.min(...aggregatedDates);
    const maxAggregatedTime = Math.max(...aggregatedDates);
    const clampedTime = Math.max(
      minAggregatedTime,
      Math.min(maxAggregatedTime, dateTime),
    );

    // Find the corresponding original data point to preserve isProjected flag
    const originalPoint = originalData.find((row) => row.Date === dateStr);

    return {
      Date: dateStr,
      Estimate: estimateSpline.at(clampedTime),
      '5th Percentile': p5Spline.at(clampedTime),
      '95th Percentile': p95Spline.at(clampedTime),
      isProjected: originalPoint ? originalPoint.isProjected : false,
    };
  });
};

// Only show month label on first occurrence: Jan 1 -> "JAN", Jan 5 -> ""
const getDistinctMonthsArray = (dateValuesArray) => {
  let lastLabelIndex = -1; // Track position of last label
  let pendingMonth = null; // Track month that is skipped due to minimum gap

  return dateValuesArray.map((dateValue, index) => {
    const date = new Date(dateValue);
    const currentMonth = date.getMonth();
    const currentYear = date.getFullYear();

    // Hide labels in the last 3 positions to prevent text cutoff
    const isInLastThreePositions = index >= dateValuesArray.length - 3;
    if (isInLastThreePositions) {
      return '';
    }

    const dateString = date
      .toLocaleDateString('en-US', { month: 'short' })
      .toUpperCase();

    if (index === 0) {
      lastLabelIndex = 0;
      return dateString;
    }

    const prevDate = new Date(dateValuesArray[index - 1]);
    const prevMonth = prevDate.getMonth();
    const prevYear = prevDate.getFullYear();

    // Check if we have a pending month that can now be shown
    const gapFromLastLabel = index - lastLabelIndex;
    if (pendingMonth && gapFromLastLabel > MINIMUM_MONTH_LABEL_GAP) {
      lastLabelIndex = index;
      const result = pendingMonth;
      pendingMonth = null;
      return result;
    }

    if (currentMonth !== prevMonth || currentYear !== prevYear) {
      // Check gap from previous label - enforce minimum gap
      if (gapFromLastLabel <= MINIMUM_MONTH_LABEL_GAP) {
        // Store this month as pending instead of losing it
        pendingMonth = dateString;
        return '';
      }

      lastLabelIndex = index;
      return dateString;
    }

    return '';
  });
};

export const prepareChartData = (
  dataFromGoogleSheets,
  dayInterval = DEFAULT_SMOOTHING_INTERVAL,
) => {
  // Handle empty data
  if (!dataFromGoogleSheets || dataFromGoogleSheets.length === 0) {
    return {
      labels: [],
      dataMean: [],
      dataP5: [],
      dataP95: [],
      fullDateLabels: [],
      tooltipDataMap: {},
      maxYScale: 20, // Default scale
      projectedFlags: [],
    };
  }

  // Process all data normally through aggregation and interpolation
  const aggregatedData = aggregateDataByDays(dataFromGoogleSheets, dayInterval);

  // For single data point or insufficient data for interpolation, use aggregated data directly
  const processedData =
    aggregatedData.length < 2
      ? aggregatedData
      : interpolateDailyValues(dataFromGoogleSheets, aggregatedData);

  // Replace the last processed point with original Google Sheets data to avoid mismatch
  if (dataFromGoogleSheets.length > 0 && processedData.length > 0) {
    const originalLatestPoint =
      dataFromGoogleSheets[dataFromGoogleSheets.length - 1];
    processedData[processedData.length - 1] = originalLatestPoint;
  }

  const fullDateLabels = [];
  const dataMean = [];
  const dataP5 = [];
  const dataP95 = [];
  const projectedFlags = [];

  processedData.map((row) => {
    fullDateLabels.push(row.Date);
    dataMean.push(row.Estimate);
    dataP5.push(row['5th Percentile']);
    dataP95.push(row['95th Percentile']);
    projectedFlags.push(row.isProjected || false);
  });

  const stepSize = Y_AXIS_TICK_RATE; // 4
  const maxDatasetValue = Math.max(...dataP95);

  // Round up to next stepSize multiple for comfortable Y-axis spacing
  const minRequired = maxDatasetValue + stepSize / 2;
  const maxYScale = Math.ceil(minRequired / stepSize) * stepSize;

  // Create data map for tooltips - now includes the unprocessed latest data point
  const tooltipDataMap = {};
  processedData.forEach((row) => {
    tooltipDataMap[row.Date] = {
      Estimate: Math.round(row.Estimate),
      '5th Percentile': Math.round(row['5th Percentile']),
      '95th Percentile': Math.round(row['95th Percentile']),
    };
  });

  return {
    labels: getDistinctMonthsArray(fullDateLabels),
    dataMean,
    dataP5,
    dataP95,
    fullDateLabels,
    tooltipDataMap,
    maxYScale,
    projectedFlags,
  };
};

// Extract and format latest processing time data
export const formatLatestProcessingTime = (dataFromGoogleSheets) => {
  try {
    if (!dataFromGoogleSheets || dataFromGoogleSheets.length === 0) {
      return 'Processing times vary';
    }

    // Get the latest date's data (last item in array)
    const latestData = dataFromGoogleSheets[dataFromGoogleSheets.length - 1];
    const p5 = latestData['5th Percentile'];
    const p95 = latestData['95th Percentile'];

    // Validate the data
    if (
      typeof p5 !== 'number' ||
      typeof p95 !== 'number' ||
      isNaN(p5) ||
      isNaN(p95)
    ) {
      return 'Processing times vary';
    }

    return `${p5}-${p95} business days`;
  } catch (error) {
    console.log('Processing time format error, using fallback', error);
    return 'Processing times vary';
  }
};

// Update processing time text element if it exists
export const updateProcessingTimeText = (dataFromGoogleSheets) => {
  try {
    const element = document.getElementById('ein-processing-time');
    if (!element) {
      // Element doesn't exist, silently skip
      return;
    }

    const formattedText = formatLatestProcessingTime(dataFromGoogleSheets);
    element.textContent = formattedText;

    // Remove is-loading class if present to switch to final color
    element.classList.remove('is-loading');
  } catch (error) {
    console.log('Processing time text update failed, using fallback', error);
    const element = document.getElementById('ein-processing-time');
    if (element) {
      element.textContent = 'Data unavailable';
    }
  }
};

// Draw origin tick mark at chart bottom-left corner
export const originTickPlugin = {
  id: 'originTick',
  beforeDraw: (chart) => {
    const ctx = chart.ctx;
    const xAxis = chart.scales.x;
    const yAxis = chart.scales.y;

    const originX = xAxis.getPixelForValue(0);
    const originY = yAxis.getPixelForValue(0);

    ctx.save();
    ctx.strokeStyle = COLORS.axisStroke;
    ctx.lineWidth = 1;
    ctx.beginPath();

    // Horizontal line of +
    ctx.moveTo(originX - 35, originY);
    ctx.lineTo(originX, originY);

    // Vertical line of +
    ctx.moveTo(originX, originY);
    ctx.lineTo(originX, originY + 20);

    ctx.stroke();
    ctx.restore();
  },
};
